import React, { memo, Suspense, useCallback, useMemo, useState } from 'react';
import times from 'lodash/times';

import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleFill, Close, TargetArrowOutline } from '@lyearn/icons';
import { AddBlock, AlertDialog, TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Loader, Popover, Skeleton, Stack, Typography } from '@lyearn/ui';
import { GoalEntityConfig } from '@/components/FilterManager/EntityFilter/entity/goals';
import FilterBody from '@/components/FilterNew/components/Body';
import { NewFilterOption } from '@/components/FilterNew/types';
import { useGoalsLightQuery } from '@/modules/corporate/modules/admin/views/AICourseCreation/graphql/queries/generated/goalsLight';

import { AttachGoalsContentProps, AttachGoalsUiState } from './type';

const translations = {
  Title: 'Common:Form.Goals',
  Add: 'Skills:AttachGoals.AddGoals',
  AttachActionLabel: 'Skills:AttachGoals.AttachActionLabel',
  AttachTitle: 'Skills:AttachGoals.AttachTitle',
  AttachConfirm: 'Skills:AttachGoals.AttachConfirm',
  DeleteActionLabel: 'Skills:AttachGoals.DeleteActionLabel',
  DeleteTitle: 'Skills:AttachGoals.DeleteTitle',
  DeleteConfirm: 'Skills:AttachGoals.DeleteConfirm',
  ChangeCallout: 'Skills:AttachGoals.ChangeCallout',
};

const AttachGoalsContent: React.FC<AttachGoalsContentProps> = (props) => {
  const {
    selectedGoalIds,
    onChange,
    disabled = false,
    readOnly = false,
    showConfirmBeforeAdd = true,
    showConfirmBeforeRemove = true,
    showChangeCallout = true,
  } = props;
  const { t } = useTranslation('Skills');

  const [uiState, setUiState] = useState<AttachGoalsUiState | null>(null);
  const showConfirmDeleteModal = useCallback((goalId: string) => {
    setUiState({
      type: 'confirm-delete',
      payload: {
        goalId,
      },
    });
  }, []);
  const showConfirmAddModal = useCallback((goalIds: string[]) => {
    setUiState({
      type: 'confirm-add',
      payload: {
        goalIds,
      },
    });
  }, []);
  const closeModal = useCallback(() => setUiState(null), []);

  const [{ data: selectedGoalData, fetching: isFetching }] = useGoalsLightQuery({
    variables: {
      filters: {
        goalIds: selectedGoalIds,
      },
    },
    pause: !selectedGoalIds.length,
  });

  const selectedGoals = useMemo(
    () =>
      selectedGoalIds.length > 0
        ? selectedGoalData?.goals?.edges?.map((edge) => edge.node) ?? []
        : [],
    [selectedGoalData?.goals?.edges, selectedGoalIds.length],
  );

  const onRemove = (goalId: string) => {
    const newGoalIds = selectedGoalIds.filter((id) => id !== goalId);
    onChange(newGoalIds);
  };

  const onRemoveGoalClick = (goalId: string) => {
    if (disabled) {
      return;
    }
    if (showConfirmBeforeRemove) {
      showConfirmDeleteModal(goalId);
    } else {
      onRemove(goalId);
    }
  };

  const onAdd = (goalIds: string[]) => {
    onChange(goalIds);
  };

  const onAddGoalClick = (values: NewFilterOption[]) => {
    if (disabled) {
      return;
    }
    const goalIds = values.map((v) => v.value);
    if (showConfirmBeforeAdd) {
      showConfirmAddModal(goalIds);
    } else {
      onAdd(goalIds);
    }
  };

  const entityConfig = GoalEntityConfig;
  const selectedFilterValuesData = entityConfig.useFilterValues({ value: selectedGoalIds });

  return (
    <>
      <Suspense fallback={<Loader />}>
        {showChangeCallout ? (
          <Stack
            alignItems="center"
            className="bg-bg-warning-light px-16 py-10 mb-24"
            direction="row"
            gap={12}>
            <AlertTriangleFill
              className="shrink-0"
              fillColorToken="text-warning-dark"
              height={16}
              width={16}
            />
            <Typography color="text-text-warning-dark" variant="body-short-02">
              {t(translations.ChangeCallout)}
            </Typography>
          </Stack>
        ) : null}
        <Stack className={cn('flex-1 flex-wrap')} direction="row" gap={12}>
          {isFetching
            ? times(selectedGoalIds.length, (index) => (
                <Box
                  key={index}
                  className={cn(
                    'flex h-40 max-w-full items-center justify-between truncate rounded-8 border px-12 border-divider',
                    { 'pointer-events-none bg-bg-secondary opacity-50': disabled },
                  )}>
                  <Stack alignItems="center" className="truncate">
                    <TargetArrowOutline
                      className="h-18 w-18 shrink-0"
                      fillColorToken="icon-secondary"
                    />
                    <Typography
                      className="mx-8 truncate"
                      color="text-text-secondary"
                      variant="body-short-02">
                      <Skeleton className="mx-8 w-[10rem]" variant="text" />
                    </Typography>
                  </Stack>
                </Box>
              ))
            : null}
          {selectedGoals.map((goal) => {
            return (
              <Box
                key={goal.id}
                className={cn(
                  'flex h-40 max-w-full items-center justify-between truncate rounded-8 border px-12 border-divider',
                  { 'pointer-events-none bg-bg-secondary opacity-50': disabled },
                )}>
                <Stack alignItems="center" className="truncate">
                  <TargetArrowOutline
                    className="h-18 w-18 shrink-0"
                    fillColorToken="icon-secondary"
                  />
                  <TypographyWithTooltip
                    className="mx-8 truncate"
                    color="text-text-secondary"
                    numberOfLines={1}
                    variant="body-short-02">
                    {goal.name}
                  </TypographyWithTooltip>
                </Stack>
                {disabled || readOnly ? null : (
                  <LinkButton
                    className="group flex-shrink-0 py-2 px-4 ps-0 pe-0 ms-8"
                    color={'internal'}
                    onClick={() => onRemoveGoalClick(goal.id)}>
                    <Close className="h-20 w-20 text-icon-placeholder group-hover:text-icon-primary" />
                  </LinkButton>
                )}
              </Box>
            );
          })}
          {readOnly ? null : (
            <Popover>
              {({ open, onOpenChange }) => (
                <>
                  <Popover.Button disabled={disabled}>
                    <AddBlock length="short" title={t(translations.Add)} />
                  </Popover.Button>
                  {open ? (
                    <Popover.Content position="bottom" renderAs="popover">
                      <FilterBody
                        initialSelectedOptions={selectedFilterValuesData.selectedOptions}
                        listConfig={entityConfig}
                        onChange={onAddGoalClick}
                        onToggle={onOpenChange}
                      />
                    </Popover.Content>
                  ) : null}
                </>
              )}
            </Popover>
          )}
        </Stack>
        {uiState?.type === 'confirm-delete' ? (
          <AlertDialog
            header={{
              title: t(translations.DeleteTitle),
              description: t(translations.DeleteConfirm),
            }}
            primaryAction={{
              appearance: 'danger',
              label: t(translations.DeleteActionLabel),
              onClick: () => {
                onRemove(uiState?.payload.goalId);
                closeModal();
              },
            }}
            onClose={closeModal}></AlertDialog>
        ) : null}
        {uiState?.type === 'confirm-add' ? (
          <AlertDialog
            header={{
              title: t(translations.AttachTitle),
              description: t(translations.AttachConfirm),
            }}
            primaryAction={{
              label: t(translations.AttachActionLabel),
              onClick: () => {
                onAdd(uiState?.payload.goalIds);
                closeModal();
              },
            }}
            onClose={closeModal}></AlertDialog>
        ) : null}
      </Suspense>
    </>
  );
};

export default memo(AttachGoalsContent);
